import { useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from 'styled-components';

import { ButtonB2B } from '~/components/Menu/ButtonB2B';
import { MenuDesktop } from '~/components/Menu/MenuDesktop';
import { MenuDropdownItems } from '~/components/Menu/MenuDropdownItems';
import { MenuMobile } from '~/components/Menu/MenuMobile';
import type { Page } from '~/config/businessUnits';
import { useListsContext } from '~/contexts/ListsContext/ListsContext';
import { useTranslation } from '~/hooks';
import { useHref } from '~/hooks/useHref';
import { getMediaQuery } from '~/utils/responsive/media';

const MenuDesktopWrapper = styled.div`
  flex: 1;
  ${getMediaQuery('mobile', 'tablet')} {
    display: none;
  }
`;

const MenuMobileWrapper = styled.div`
  ${getMediaQuery('laptop', 'desktop')} {
    display: none;
  }
`;

const ButtonB2BWrapper = styled.div`
  display: flex;
  justify-content: center;
  ${getMediaQuery('mobile', 'tablet')} {
    flex: 1;
  }
`;

export const MENU_MAX_ITEMS_COUNT = 20;

export const Menu = () => {
  const t = useTranslation();
  const { getPageHref } = useHref();
  const [headerHeight, setHeaderHeight] = useState(0);
  const { cityList, jobList, sectorList } = useListsContext();
  const [selectedMenuName, setSelectedMenuName] = useState<string>();

  useEffect(() => {
    const header = document.querySelector('header');

    const updateHeaderTop = () => {
      if (header) {
        const rect = header.getBoundingClientRect();
        setHeaderHeight(rect.bottom);
      }
    };

    const handleScroll = () => {
      updateHeaderTop();
    };

    updateHeaderTop();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeaderTop);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeaderTop);
    };
  }, []);

  const formatListToItems = useCallback(
    (page: Page, list: string[]) =>
      list.slice(0, MENU_MAX_ITEMS_COUNT).map(label => ({ label, href: getPageHref(page, label) })),
    [getPageHref],
  );

  const menuItems = useMemo(
    () => [
      {
        name: 'sectors',
        items: formatListToItems('sectors', sectorList),
        link: getPageHref('sectors'),
      },
      {
        name: 'cities',
        items: formatListToItems('cities', cityList),
        link: getPageHref('cities'),
      },
      {
        name: 'jobs',
        items: formatListToItems('jobs', jobList),
        link: getPageHref('jobs'),
      },
    ],
    [t, formatListToItems],
  );

  return (
    <>
      <MenuDesktopWrapper>
        <MenuDesktop
          menuItems={menuItems}
          selectedMenuName={selectedMenuName}
          setSelectedMenuName={setSelectedMenuName}
        />
      </MenuDesktopWrapper>
      <ButtonB2BWrapper>
        <ButtonB2B />
      </ButtonB2BWrapper>
      <MenuMobileWrapper>
        <MenuMobile
          menuItems={menuItems}
          headerHeight={headerHeight}
          selectedMenuName={selectedMenuName}
          setSelectedMenuName={setSelectedMenuName}
        />
      </MenuMobileWrapper>
      <MenuDropdownItems
        menuItems={menuItems}
        headerHeight={headerHeight}
        selectedMenuName={selectedMenuName}
        setSelectedMenuName={setSelectedMenuName}
      />
    </>
  );
};
